import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Modal, message, Alert } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Auth } from 'aws-amplify';
import { getPendingLicenseApprovals } from '../../graphql/queries';
import { approvePendingLicense, rejectPendingLicense } from '../../graphql/mutations';
import { axiosRequest } from '../../utils/axiosRequest';
import styles from './SupplierAdminPortal.module.css';

const getHeaders = async () => {
  const session = await Auth.currentSession();
  const idToken = session.getIdToken();
  const jwt = idToken.getJwtToken();
  return {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
    Authorization: jwt,
  };
};

export const PendingLicenseApprovals = () => {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchApprovals = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await getHeaders();
      const body = {
        query: getPendingLicenseApprovals,
        variables: {},
      };
      const res = await axiosRequest({ headers, body, method: 'post' });
      setApprovals(res.data?.data?.getPendingLicenseApprovals?.data || []);
    } catch (error) {
      console.error('Error fetching approvals:', error);
      message.error('Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  const handleApprove = (approval) => {
    Modal.confirm({
      title: 'Approve License Request',
      content: (
        <div>
          <p>Approve license for:</p>
          <p><strong>{approval.userFirstName} {approval.userLastName}</strong></p>
          <p>{approval.userEmail}</p>
          <p>Vendor: {approval.vendorGroupName}</p>
        </div>
      ),
      okText: 'Approve',
      okType: 'primary',
      onOk: async () => {
        try {
          const headers = await getHeaders();
          const body = {
            query: approvePendingLicense,
            variables: {
              approvalId: approval.approvalId,
              comments: null,
              firstName: approval.userFirstName,
              lastName: approval.userLastName,
            },
          };
          await axiosRequest({ headers, body, method: 'post' });
          message.success('License approved successfully');
          fetchApprovals(); // Refresh list
        } catch (error) {
          console.error('Error approving license:', error);
          message.error('Failed to approve license');
        }
      },
    });
  };

  const handleReject = async (approval) => {
    Modal.confirm({
      title: 'Reject License Request',
      content: (
        <div>
          <p>Reject license for:</p>
          <p><strong>{approval.userFirstName} {approval.userLastName}</strong></p>
          <p>{approval.userEmail}</p>
          <p>Vendor: {approval.vendorGroupName}</p>
        </div>
      ),
      okText: 'Reject',
      okType: 'danger',
      onOk: async () => {
        try {
          const headers = await getHeaders();
          const body = {
            query: rejectPendingLicense,
            variables: {
              approvalId: approval.approvalId,
            },
          };
          await axiosRequest({ headers, body, method: 'post' });
          message.success('License request rejected');
          fetchApprovals(); // Refresh list
        } catch (error) {
          console.error('Error rejecting license:', error);
          message.error('Failed to reject license');
        }
      },
    });
  };

  const columns = [
    {
      title: 'Vendor',
      dataIndex: 'vendorGroupName',
      key: 'vendorGroupName',
      sorter: (a, b) => a.vendorGroupName.localeCompare(b.vendorGroupName),
    },
    {
      title: 'User',
      key: 'user',
      render: (_, record) => (
        <div>
          <div><strong>{record.userFirstName} {record.userLastName}</strong></div>
          <div className={styles['user-email-cell']}>{record.userEmail}</div>
        </div>
      ),
    },
    {
      title: 'Requested By',
      dataIndex: 'requestedByEmail',
      key: 'requestedByEmail',
    },
    {
      title: 'Request Date',
      dataIndex: 'requestTimestamp',
      key: 'requestTimestamp',
      render: (date) => new Date(date).toLocaleString(),
      sorter: (a, b) => new Date(a.requestTimestamp) - new Date(b.requestTimestamp),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            onClick={() => handleApprove(record)}
          >
            Approve
          </Button>
          <Button
            danger
            size="small"
            icon={<CloseOutlined />}
            onClick={() => handleReject(record)}
          >
            Reject
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className={styles['pending-license-approvals']}>
      <h1>Pending License Approvals</h1>
      <p className={styles.subtitle}>
        Review and approve supplier admin license requests
      </p>

      {approvals.length === 0 && !loading && (
        <Alert
          message="No Pending Approvals"
          description="There are currently no pending license approval requests."
          type="info"
          showIcon
          className={styles['mb-16']}
        />
      )}

      {approvals.length > 0 && !loading && (
        <Table
          columns={columns}
          dataSource={approvals}
          loading={loading}
          rowKey="approvalId"
          pagination={{ pageSize: 20 }}
        />
      )}
    </div>
  );
};
